import { Component, OnInit, Inject, PLATFORM_ID, TransferState, makeStateKey, Renderer2 } from '@angular/core';
import { isPlatformBrowser, isPlatformServer, DOCUMENT } from '@angular/common';
import { environment } from '../../../environments/environment';
import { CartService } from '../../_services/cart.service';
import { Observable } from 'rxjs';
import { ProductModel } from '../../_cart/product-model';
import { OfferService } from '../../_services/offer.service';
import { ActivatedRoute, Router } from '@angular/router';
import { getDiscountPrice, getPrice, isOfferInPromotion, Offer } from '../../_models/offer';
import { User } from '../../_models/user';
import { StoreService } from 'src/app/_services/store.service';
import { TranslateService } from '@ngx-translate/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { UserService } from 'src/app/_services/user.service';
import { ToastrService } from 'ngx-toastr';
import { switchMap, take } from 'rxjs/operators';
import { Title, Meta } from '@angular/platform-browser';
import { WishlistService } from '../../_services/wishlist.service';
import { WishList } from '../../_models/wish-list';

declare var $: any;

@Component({
    selector: 'app-single-product',
    templateUrl: './single-product.component.html',
    styleUrls: ['./single-product.component.css'],
    standalone: false
})
export class SingleProductComponent implements OnInit {


    baseUrl = environment.baseUrl;

    // ── review state ──────────────────────────────────────────────────────────
    reviewStars = 0;
    finalTitle = '';
    reviewForm: FormGroup;
    showQty = false;

    // ── gallery slider state ──────────────────────────────────────────────────
    /** Index of the currently visible slide */
    activeSlide = 0;
    /** Tracks zoom hover state */
    isZoomed = false;
    /** X position captured on touchstart for swipe detection */
    private touchStartX = 0;

    // ── product / offer state ─────────────────────────────────────────────────
    offer: Offer = null;
    offerId: any;
    user: User;
    mediaList = [];
    products: Observable<ProductModel[]>;
    productModel: ProductModel = null;
    relatedProducts: any[];
    reviews: any = null;
    displayReviews: any[] = [];
    relatedOffersLoading = true;
    showMoreButton = false;
    totalPage = 1;
    currentReviewPage = 1;
    loading = true;
    isInWishlist = false;
    userWishlist: WishList[] = [];

    // ── share URLs ────────────────────────────────────────────────────────────
    facebookShareUrl: string;
    twitterShareUrl: string;
    linkedinShareUrl: string;
    whatsappShareUrl: string;

    // ── TransferState keys ────────────────────────────────────────────────────
    private readonly OFFER_DETAIL_KEY  = makeStateKey<any>('product_detail');
    private readonly RELATED_OFFERS_KEY = makeStateKey<any[]>('product_related_offers');

    // ── shipping options labels ───────────────────────────────────────────────
    shippingTypeLabels: { [key: string]: string } = {};

    constructor(
        private cartService: CartService,
        public userService: UserService,
        private toastService: ToastrService,
        private storeService: StoreService,
        private activatedRoute: ActivatedRoute,
        private router: Router,
        private titleService: Title,
        private meta: Meta,
        private offerService: OfferService,
        private translateService: TranslateService,
        private wishlistService: WishlistService,
        public fb: FormBuilder,
        @Inject(PLATFORM_ID) private platformId: Object,
        private transferState: TransferState,
        private renderer: Renderer2,
        @Inject(DOCUMENT) private document: Document
    ) {
        this.products = this.cartService.getProducts();

        if (isPlatformBrowser(this.platformId)) {
            this.user = JSON.parse(localStorage.getItem('currentUser') ?? '{}');
        }

        this.offerId     = this.activatedRoute.snapshot.paramMap.get('id') ?? '';
        this.productModel = this.storeService.findItem(this.offerId);

        if (isPlatformBrowser(this.platformId)) {
            window.scrollTo(0, 0);
        }

        this.reviewForm = this.fb.group({
            review: [''],
            rating: []
        });
    }

    /**
     * True when the active UI language is French.
     * Reads currentLang first, then falls back to defaultLang (the app only calls
     * setDefaultLang at startup; currentLang stays undefined until a language toggle
     * is clicked), then 'fr'. Evaluated on every change detection so it reacts to
     * language switches made while the page is open.
     */
    get usingFrench(): boolean {
        const lang = this.translateService.currentLang || this.translateService.defaultLang || 'fr';
        return lang === 'fr';
    }

    // ═══════════════════════════════════════════════════════════════════════════
    //  LIFECYCLE
    // ═══════════════════════════════════════════════════════════════════════════

    ngOnInit() {
        if (this.productModel && this.productModel.qty > 0) {
            this.showQty = true;
        }

        this.translateService.get([
            'product.shipping_type_international',
            'product.shipping_type_intercity',
            'product.shipping_type_local'
        ]).subscribe(translations => {
            this.shippingTypeLabels['INTERNATIONAL'] = translations['product.shipping_type_international'];
            this.shippingTypeLabels['INTERCITY']      = translations['product.shipping_type_intercity'];
            this.shippingTypeLabels['LOCAL']           = translations['product.shipping_type_local'];
        });

        this.checkPendingWishlistAction();

        if (isPlatformBrowser(this.platformId) && this.user && this.user.id) {
            this.loadUserWishlist();
        }

        // Use TransferState cache (server → browser hand-off)
        if (this.transferState.hasKey(this.OFFER_DETAIL_KEY)) {
            this.offer = this.transferState.get(this.OFFER_DETAIL_KEY, null);
            this.transferState.remove(this.OFFER_DETAIL_KEY);

            if (this.offer) {
                this.mediaList = this.offer.mediaList;
                this.activeSlide = 0;
                this.finalTitle = this.buildTitle(this.offer);
                this.titleService.setTitle(this.finalTitle);
                this.setMetaTags();
                this.generateShareUrls();

                if (this.transferState.hasKey(this.RELATED_OFFERS_KEY)) {
                    this.relatedProducts = this.transferState.get(this.RELATED_OFFERS_KEY, []);
                    this.relatedOffersLoading = false;
                    this.transferState.remove(this.RELATED_OFFERS_KEY);
                } else {
                    this.offerService.getRelatedOffers(this.offer.product.category.id, this.offer.id)
                        .subscribe(r => {
                            this.relatedProducts = r;
                            this.relatedOffersLoading = false;
                        });
                }
            }
            this.loading = false;

        } else {
            // Fetch from API
            this.offerService.loadOffer(this.offerId)
                .pipe(
                    switchMap((offer) => {
                        this.offer = offer;
                        this.mediaList = offer.mediaList;
                        this.activeSlide = 0;
                        this.finalTitle = this.buildTitle(offer);
                        this.titleService.setTitle(this.finalTitle);
                        this.setMetaTags();
                        this.generateShareUrls();

                        if (isPlatformServer(this.platformId)) {
                            this.transferState.set(this.OFFER_DETAIL_KEY, offer);
                        }

                        return this.offerService.getRelatedOffers(this.offer.product.category.id, this.offer.id);
                    })
                )
                .subscribe(r => {
                    this.relatedProducts = r;
                    this.relatedOffersLoading = false;

                    if (isPlatformServer(this.platformId)) {
                        this.transferState.set(this.RELATED_OFFERS_KEY, r);
                    }

                    this.addProductSchema();
                });
        }

        // Reviews — client-side only
        if (isPlatformBrowser(this.platformId)) {
            this.loadReviews(this.currentReviewPage).subscribe(res => {
                if (res.reviews.length > 0) {
                    this.displayReviews.push(...res.reviews);
                    this.totalPage = res.totalPage;
                }
                this.reviews = res;
                this.loading = false;
            });
        } else {
            this.loading = false;
        }

        this.router.routeReuseStrategy.shouldReuseRoute = () => false;

        if (isPlatformBrowser(this.platformId)) {
            window.scrollTo(0, 0);
        }
    }

    // ═══════════════════════════════════════════════════════════════════════════
    //  GALLERY / SLIDER METHODS
    // ═══════════════════════════════════════════════════════════════════════════

    /** Navigate to a specific slide index */
    goToSlide(index: number): void {
        if (index >= 0 && index < this.mediaList.length) {
            this.activeSlide = index;
        }
    }

    /** Go to the previous slide (wraps around) */
    prevSlide(): void {
        this.activeSlide = (this.activeSlide - 1 + this.mediaList.length) % this.mediaList.length;
    }

    /** Go to the next slide (wraps around) */
    nextSlide(): void {
        this.activeSlide = (this.activeSlide + 1) % this.mediaList.length;
    }

    /** Capture touch start X for swipe detection */
    onTouchStart(event: TouchEvent): void {
        this.touchStartX = event.touches[0].clientX;
    }

    /** Detect left/right swipe and navigate accordingly */
    onTouchEnd(event: TouchEvent): void {
        const deltaX = event.changedTouches[0].clientX - this.touchStartX;
        const SWIPE_THRESHOLD = 40;
        if (Math.abs(deltaX) > SWIPE_THRESHOLD) {
            deltaX < 0 ? this.nextSlide() : this.prevSlide();
        }
    }

    // ═══════════════════════════════════════════════════════════════════════════
    //  IMAGE HELPERS
    // ═══════════════════════════════════════════════════════════════════════════

    getImageSrc(imagePath: string): string {
        return imagePath && imagePath !== '' && imagePath.includes('media')
            ? `${environment.image_cdn_url}/${imagePath}?func=fit&trim=0&width=600&height=600`
            : '/assets/images/default.jpg';
    }

    printImage(product: any): string {
        return this.getImageSrc(product.picture);
    }

    /**
     * Builds a display name for the seller, tolerating missing names.
     * Filters out null/undefined/blank parts (and literal "null"/"undefined" strings
     * sent by the API), then falls back to company/contact info, then town.
     */
    getSellerName(user: User): string {
        if (!user) return '';
        const clean = (v: any) => (v ?? '').toString().trim();
        const invalid = new Set(['', 'null', 'undefined']);
        const parts = [user.firstName, user.lastName]
            .map(clean)
            .filter(p => !invalid.has(p.toLowerCase()));
        if (parts.length > 0) {
            return parts.join(' ');
        }
        const companyName = user.seller && user.seller.sellerType === 'company' ? clean(user.seller.companyName) : '';
        return companyName || clean(user.contactName) || clean(user.town) || '—';
    }

    /** @deprecated — kept for any remaining template references */
    checkIfLazy(productImage: any): boolean {
        const el = productImage?.nativeElement ?? productImage;
        return !el?.classList.contains('ng-lazyloaded');
    }

    // ═══════════════════════════════════════════════════════════════════════════
    //  TITLE / META
    // ═══════════════════════════════════════════════════════════════════════════

    private buildTitle(offer: Offer): string {
        return (this.usingFrench
            ? `${offer.product.productNameFr} en vente à ${offer.user.town}`
            : `${offer.product.name} on sale in ${offer.user.town}`) + ' | eFarm.cm';
    }

    private setMetaTags(): void {
        if (!this.offer) { return; }

        const rawDescription = this.offer.description || `${this.offer.product.name} - ${this.offer.user.town}`;
        let description = this.stripHtml(rawDescription);
        if (description.length > 200) {
            description = description.substring(0, 197).trim() + '...';
        }

        const imageUrl = this.offer.defaultPicture
            ? `${environment.image_cdn_url}/${this.offer.defaultPicture}?func=fit&width=1200&height=630`
            : `${environment.image_cdn_url}/${this.offer.product.picture}`;

        this.titleService.setTitle(this.finalTitle);

        this.meta.updateTag({ property: 'og:title',       content: this.finalTitle });
        this.meta.updateTag({ property: 'og:description', content: description });
        this.meta.updateTag({ property: 'og:image',       content: imageUrl });
        this.meta.updateTag({ property: 'og:type',        content: 'product' });
        this.meta.updateTag({ property: 'og:site_name',   content: 'eFarm Marketplace' });

        this.meta.updateTag({ name: 'twitter:card',        content: 'summary_large_image' });
        this.meta.updateTag({ name: 'twitter:title',       content: this.finalTitle });
        this.meta.updateTag({ name: 'twitter:description', content: description });
        this.meta.updateTag({ name: 'twitter:image',       content: imageUrl });
        this.meta.updateTag({ name: 'description',         content: description });
    }

    // ═══════════════════════════════════════════════════════════════════════════
    //  SHARE URLS
    // ═══════════════════════════════════════════════════════════════════════════

    private generateShareUrls(): void {
        if (!isPlatformBrowser(this.platformId) || !this.offer) { return; }

        const currentUrl   = encodeURIComponent(window.location.href);
        const productName  = encodeURIComponent(this.usingFrench ? this.offer.product.productNameFr : this.offer.product.name);
        const price        = encodeURIComponent(this.getOfferPrice(this.offer).toString());
        const currency     = encodeURIComponent(this.offer.currency.toUpperCase());

        let twitterText  = this.translateService.instant('share.twitterText');
        if (twitterText  === 'share.twitterText')  { twitterText  = this.usingFrench ? 'Découvrez ce produit sur eFarm.cm:' : 'Check out this product on eFarm.cm:'; }

        let whatsappText = this.translateService.instant('share.whatsappText');
        if (whatsappText === 'share.whatsappText') { whatsappText = this.usingFrench ? 'Découvrez ce produit sur eFarm.cm:' : 'Check out this product on eFarm.cm:'; }

        const encTwitter  = encodeURIComponent(twitterText);
        const encWhatsapp = encodeURIComponent(whatsappText);

        this.facebookShareUrl = `https://www.facebook.com/sharer/sharer.php?u=${currentUrl}`;
        this.twitterShareUrl  = `https://x.com/intent/tweet?url=${currentUrl}&text=${encTwitter}%20${productName}%20-%20${price}%20${currency}&hashtags=eFarm`;
        this.linkedinShareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${currentUrl}`;
        this.whatsappShareUrl = `https://wa.me/?text=${encWhatsapp}%0A%0A${productName}%0A${price}%20${currency}%0A${currentUrl}`;
    }

    openShareWindow(url: string, platform: string): void {
        if (!isPlatformBrowser(this.platformId) || !url || url === 'undefined' || url === 'null') { return; }
        const w = 600, h = 400;
        const left = (window.innerWidth  - w) / 2;
        const top  = (window.innerHeight - h) / 2;
        window.open(url, `share-${platform}`, `width=${w},height=${h},left=${left},top=${top},toolbar=no,menubar=no`);
    }

    // ═══════════════════════════════════════════════════════════════════════════
    //  CART
    // ═══════════════════════════════════════════════════════════════════════════

    addProductToCart(offer: any): void {
        this.showQty = true;
        this.cartService.addToLocalCart(offer).subscribe(() => {
            this.productModel = this.storeService.findItem(this.offerId);
        });
    }

    incrementQty(offer: Offer): void { this.addProductToCart(offer); }

    decrementQty(offer: Offer): void {
        if (!this.productModel) { return; }
        if (this.productModel.qty === 1) {
            this.productModel = null;
            this.storeService.removeItemFromStore(offer.id).subscribe();
            this.showQty = false;
        } else {
            this.cartService.decrementQty(this.productModel.id).subscribe(() => {
                this.productModel = this.storeService.findItem(this.offerId);
            });
        }
    }

    getCurrentQty(offer: Offer, products: ProductModel[]): number {
        if (!products) { return 0; }
        const idx = products.findIndex(p => p.id === offer.id);
        const qty = idx >= 0 ? products[idx].qty : 0;
        if (isPlatformBrowser(this.platformId)) { $('#qty').val(qty); }
        return qty;
    }

    changeQty($event: any, offer: Offer, products: any): void {
        const value = parseInt($event.target.value, 10);
        if (value === 0) {
            this.productModel = null;
            this.storeService.removeItemFromStore(offer.id).subscribe();
            this.showQty = false;
        } else {
            this.cartService.addToLocalCart(offer, value).subscribe(() => {
                this.productModel = this.storeService.findItem(this.offerId);
            });
        }
    }

    buyNow(offer: Offer): void {
        this.cartService.addToLocalCart(offer).subscribe(() => {
            this.cartService.selectOnly(offer.id);
            this.router.navigateByUrl('/delivery');
        });
    }

    // ═══════════════════════════════════════════════════════════════════════════
    //  REVIEWS
    // ═══════════════════════════════════════════════════════════════════════════

    loadReviews(page: number) {
        return this.offerService.getProductReviews(this.offerId, page);
    }

    showMore(): void {
        if (this.reviews.totalPage > this.currentReviewPage) {
            this.loadReviews(++this.currentReviewPage);
            if (this.reviews.totalPage === this.totalPage) {
                this.showMoreButton = false;
            }
        }
    }

    submitReviewForm(): void {
        const formData = {
            nbStars: this.reviewForm.value.rating,
            remark:  this.reviewForm.value.review
        };

        this.offerService.submitReview(this.offer.id, formData).subscribe(
            () => {
                this.reviewForm.reset();
                if (isPlatformBrowser(this.platformId)) { window.location.reload(); }
            },
            (error: any) => {
                if (error === 'Accès refusé') {
                    this.toastService.error('Please log in to review this product');
                    this.router.navigateByUrl('/signin');
                }
            }
        );
    }

    // ═══════════════════════════════════════════════════════════════════════════
    //  WISHLIST
    // ═══════════════════════════════════════════════════════════════════════════

    addToWishlist(): void {
        if (!isPlatformBrowser(this.platformId)) { return; }

        if (!this.user || !this.user.id) {
            localStorage.setItem('pendingWishlistOfferId', this.offerId);
            this.router.navigate(['/signin'], { queryParams: { returnUrl: this.router.url } });
            return;
        }

        this.wishlistService.addToWishList(this.user.id, parseInt(this.offerId, 10)).subscribe({
            next: () => {
                this.isInWishlist = true;
                this.translateService.get('common.message_for_added_to_wishlist')
                    .pipe(take(1))
                    .subscribe(msg => this.toastService.success(msg));
            },
            error: (err) => {
                if (err.status === 409 || err.error?.message?.includes('already')) {
                    this.isInWishlist = true;
                }
            }
        });
    }

    private loadUserWishlist(): void {
        if (!this.user?.id) { return; }
        this.wishlistService.myWishList(this.user.id).subscribe({
            next: (wishlist) => {
                this.userWishlist = wishlist;
                this.isInWishlist = wishlist.some(item => item.offerId === parseInt(this.offerId, 10));
            },
            error: () => { /* silently fail — wishlist is optional */ }
        });
    }

    private checkPendingWishlistAction(): void {
        if (!isPlatformBrowser(this.platformId)) { return; }

        const pendingId = localStorage.getItem('pendingWishlistOfferId');
        if (pendingId && pendingId === this.offerId) {
            localStorage.removeItem('pendingWishlistOfferId');

            if (this.user?.id) {
                this.wishlistService.addToWishList(this.user.id, parseInt(this.offerId, 10)).subscribe({
                    next: () => {
                        this.isInWishlist = true;
                        this.translateService.get('common.message_for_added_to_wishlist')
                            .pipe(take(1))
                            .subscribe(msg => this.toastService.success(msg));
                    },
                    error: () => {}
                });
            }
        }
    }

    // ═══════════════════════════════════════════════════════════════════════════
    //  PRICING HELPERS
    // ═══════════════════════════════════════════════════════════════════════════

    isInPromotion(offer: Offer): boolean  { return isOfferInPromotion(offer); }
    getOfferPrice(offer: Offer): number   { return getPrice(offer); }
    getDiscountPrice(offer: Offer): number { return getDiscountPrice(offer); }

    // ═══════════════════════════════════════════════════════════════════════════
    //  SHIPPING OPTIONS HELPERS
    // ═══════════════════════════════════════════════════════════════════════════

    getShippingTypeLabel(ref: string): string {
        return this.shippingTypeLabels[ref] || ref;
    }

    hasShippingOptions(): boolean {
        return !!(this.offer && this.offer.shippingOptions && this.offer.shippingOptions.length > 0);
    }

    getShippingStartingAtLabel(option: any): string {
        if (!this.offer) { return ''; }
        const amount = (option.minPrice || 0).toLocaleString();
        const currency = (this.offer.currency || '').toUpperCase();
        return this.usingFrench
            ? `Livraison à partir de ${amount} ${currency}`
            : `Shipping starts at ${amount} ${currency}`;
    }

    // ─── Legacy carousel stubs (safe to remove once ngx-slick-carousel is fully out) ───
    slickInit(_e: any)    {}
    breakpoint(_e: any)   {}
    afterChange(_e: any)  {}
    beforeChange(_e: any) {}

    /** @deprecated — replaced by goToSlide() */
    changeProductImage(_current: string): void {}

    /** @deprecated — no longer used with new slider */
    setOwlCarousel(): void {}

    getProductSchema(): any {
        if (!this.offer) {
            return null;
        }

        const product = this.offer.product;
        const productUrl = this.baseUrl + 'products/' + (product.slug || product.name) + '/' + this.offer.id;

        const images = this.mediaList?.length
            ? this.mediaList.map(media => this.getImageSrc(media.path))
            : [this.printImage(product)];

        const schema: any = {
            '@context': 'https://schema.org',
            '@type': 'Product',

            '@id': this.offer.id,

            name: this.usingFrench ? product.productNameFr : product.name,

            description: (this.usingFrench ? product.productNameFr : product.name) + ' ' + (this.offer.productCondition?.description || ''),

            image: images,

            url: productUrl,

            category: this.usingFrench ? product.category?.nameFr : product.category?.name,

            offers: {
                '@type': 'Offer',

                '@id': this.offer.id,

                name: this.finalTitle,

                url: productUrl,

                price: this.getOfferPrice(this.offer),

                priceCurrency: this.offer.currency.toUpperCase(),

                availability: this.getSchemaAvailability(this.offer),

                itemCondition: this.offer.productCondition?.description || 'https://schema.org/NewCondition',

                seller: {
                    '@type': 'Organisation',
                    name:
                    (this.offer.user?.firstName || '') +
                    ' ' +
                    (this.offer.user?.lastName || '')
                }
            }
        };

        if (
            this.reviews &&
            this.reviews.nbReviews > 0 &&
            this.reviews.avgStars > 0
        ) {
            schema.aggregateRating = {
                '@type': 'AggregateRating',
                ratingValue: this.reviews.avgStars,
                reviewCount: this.reviews.nbReviews
            };
        }

        return schema;
    }

    getSchemaAvailability(offer: any): string {
        if (offer.availableDate) {
            return 'https://schema.org/PreOrder';
        }

        if (offer.qty > 0) {
            return 'https://schema.org/InStock';
        }

        return 'https://schema.org/OutOfStock';
    }

    /**
     * SSR-safe HTML stripper (no DOM access, works on server and browser).
     */
    stripHtml(html: string | null | undefined): string {
        if (!html) { return ''; }

        return html
            .replace(/<br\s*\/?>/gi, ' ')
            .replace(/<\/(p|div|li|h[1-6])>/gi, ' ')
            .replace(/<[^>]*>/g, '')
            .replace(/&nbsp;/gi, ' ')
            .replace(/&amp;/gi, '&')
            .replace(/&lt;/gi, '<')
            .replace(/&gt;/gi, '>')
            .replace(/&quot;/gi, '"')
            .replace(/&#0?39;/g, "'")
            .replace(/\s+/g, ' ')
            .trim();
    }

    private addProductSchema(): void {
        const schema = this.getProductSchema();

        if (!schema) {
            return;
        }

        const oldSchema = this.document.head.querySelector(
            'script[data-product-schema]'
        );

        if (oldSchema) {
            oldSchema.remove();
        }

        const script = this.renderer.createElement('script');

        script.type = 'application/ld+json';
        script.setAttribute('data-product-schema', 'true');
        script.text = JSON.stringify(schema);

        this.renderer.appendChild(
            this.document.head,
            script
        );
    }

}